import { ExpectedError } from "../../expectedError";
import logo from "../redisCloud/logo.svg";
import { customAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";

function connectionString({
  host,
  port,
  username,
  tls,
}: Record<string, string>) {
  const hostname = (host ?? "").trim();
  if (
    !/^[A-Za-z0-9._-]+$/.test(hostname) &&
    !/^\[[0-9a-fA-F:.]+\]$/.test(hostname)
  ) {
    throw new ExpectedError("Invalid Redis host");
  }
  try {
    new URL(`redis://${hostname}`);
  } catch {
    throw new ExpectedError("Invalid Redis host");
  }
  const portValue = port || "6379";
  if (
    !/^\d+$/.test(portValue) ||
    Number(portValue) < 1 ||
    Number(portValue) > 65535
  ) {
    throw new ExpectedError("Invalid Redis port");
  }
  if (tls && tls !== "off" && tls !== "implicit") {
    throw new ExpectedError("Invalid Redis TLS mode");
  }
  const scheme = tls === "implicit" ? "rediss" : "redis";
  return `${scheme}://${encodeURIComponent(username ?? "")}:__3B_REDIS_PASSWORD_PLACEHOLDER__@${hostname}:${portValue}`;
}

export const redis = defineConnector({
  identifier: "redis",
  name: "Redis",
  tagLine: "In-memory database and cache",
  keywords: "database cache key value",
  logo,
  tags: ["data"],
  protocol: "redis",
  connectionTest: { protocol: "redis", connectionString },
  defaultUrlPattern: () => "",
  customizeStepHandledInline: true,
  auth: customAuth("redis"),
  steps: [
    {
      title: "Enter your Redis connection",
      items: [
        {
          kind: "markdown",
          content:
            "The connection test supports Redis 7.2 and newer and requires permission to run HELLO and PING.",
        },
        {
          kind: "input",
          label: "Name",
          required: true,
          defaultValue: "Redis",
          target: { kind: "connectedApp", field: "name" },
        },
        {
          kind: "input",
          label: "Host",
          required: true,
          placeholder: "redis.example.com",
          target: { kind: "configuration", field: "host" },
        },
        {
          kind: "input",
          label: "Port",
          required: true,
          defaultValue: "6379",
          target: { kind: "configuration", field: "port" },
        },
        {
          kind: "input",
          label: "Username",
          helperText:
            "Enter an ACL username, or leave blank to use the default user.",
          target: { kind: "configuration", field: "username" },
        },
        {
          kind: "input",
          label: "Password",
          required: true,
          target: { kind: "secrets", field: "password" },
        },
        {
          kind: "select",
          label: "TLS",
          required: true,
          defaultValue: "off",
          target: { kind: "configuration", field: "tls" },
          options: [
            { value: "off", label: "Disabled" },
            { value: "implicit", label: "Enabled" },
          ],
        },
        {
          kind: "file",
          label: "CA certificate",
          accept: ".pem,.crt,.cer",
          helperText:
            "Upload a PEM CA certificate for private or self-signed Redis servers.",
          target: { kind: "configuration", field: "tlsCaCertificate" },
          visibleWhen: [
            {
              target: { kind: "configuration", field: "tls" },
              value: "implicit",
            },
          ],
        },
      ],
    },
  ],
  publicEnvVars: (configuration) => ({
    REDIS_URL: connectionString(configuration),
  }),
  usageHint: () => "Use process.env.REDIS_URL as the Redis connection string.",
});
